import { useState } from "react";

import { useTheme } from "@emotion/react";
import { Box, Button, Card, Grid, Modal, useMediaQuery } from "@mui/material";
import IconX from "assets/icon-close-modal.svg";
import roundx from "assets/roundx.png";
import SurveyCheckButton from "components/SurveyCheckButton";
import { H4, H6, H7, TableContent } from "components/Typography";
import FlexBox from "components/flexbox/FlexBox";
import FlexRowAlign from "components/flexbox/FlexRowAlign";
import AppTextField from "components/input-fields/AppTextField";
import { useFormik } from "formik";
import SurveyCompleteModal from "pages/survey-complete-modal";
import TermsModalButton from "pages/terms-modal";
import { Clock } from "react-feather";
import * as Yup from "yup";

export default function SurveyModal({ open, setShowModal }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : "48%",
    maxHeight: "90vh",
    overflowY: "auto",
    // borderRadius: 4,
    // border: "1px solid #E1E1E1",
    // p: 4,
    borderWidth: 0,
    scrollbarWidth: "thin", // Firefox
    "&::-webkit-scrollbar": {
      width: "0", // Adjust the width as needed
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent", // Hide scrollbar thumb
    },
  };

  const initialValues = {
    fullName: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zip: "",
    about: "",
  };
  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required("Name is Required!"),
    email: Yup.string().email().required("Email is Required!"),
    phone: Yup.number().min(8).required("Phone is Required!"),
    country: Yup.string().required("Country is Required!"),
    state: Yup.string().required("State is Required!"),
    city: Yup.string().required("City is Required!"),
    address: Yup.string().required("Address is Required!"),
    zip: Yup.string().required("Zip is Required!"),
    about: Yup.string().required("About is Required!"),
  });
  const { values, errors, handleChange, handleSubmit, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });

  const handleClose = () => {
    setShowModal(false);
  };

  const onSubmit = () => {
    setShowModal(false);
    setShowCompleteModal(true);
  };

  return (
    <>
      {showCompleteModal ? (
        <SurveyCompleteModal
          showModal={showCompleteModal}
          setShowModal={setShowCompleteModal}
        />
      ) : (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ backdropFilter: "blur(5px)" }}
        >
          <Grid container sx={style}>
            <Grid item xs={12}>
              <Card>
                <img
                  onClick={() => setShowModal(false)}
                  src={IconX}
                  style={{
                    width: "30px",
                    height: "30px",
                    cursor: "pointer",
                    position: "absolute",
                    top: "22px",
                    right: "22px",
                  }}
                />
                <FlexBox
                  sx={{
                    backgroundColor: theme.palette.primary.darkBlue,
                    // borderRadius: 4,
                    borderWidth: 0,
                  }}
                >
                  <Box
                    pt={3}
                    pb={6}
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      padding: isMobile
                        ? "50px 20px 32px 20px"
                        : "50px 32px 32px 32px",
                      // borderWidth: 0,
                    }}
                  >
                    <H4 mb={2.5} sx={{ color: "white" }}>
                      보험상담 설문지
                    </H4>

                    <H7 sx={{ color: "white", fontWeight: 400 }}>
                      제안하실 의견 및 문의 사항을 접수해주세요.
                    </H7>
                    <FlexRowAlign
                      gap={1}
                      mt={1}
                      sx={{
                        backgroundColor: "#2868B0",
                        padding: "7px 14px 7px 14px",
                        borderRadius: 100,
                      }}
                    >
                      <Clock
                        color="white"
                        size={18}
                        style={{ marginRight: "0px", marginBottom: "2px" }}
                      />
                      <H7 sx={{ color: "white", fontWeight: 300 }}>
                        2023.12.29. 오후 04:20 ~ 제한 없음
                      </H7>
                    </FlexRowAlign>
                  </Box>
                </FlexBox>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3} py={4} px={isMobile ? 2 : 4}>
                    <Grid item xs={12}>
                      <FlexRowAlign
                        sx={{ justifyContent: "space-between", width: "100%" }}
                      >
                        <H6 mb={1}>
                          1. 이름, 거주지역 남겨주세요
                          <span style={{ color: theme.palette.primary.red }}>
                            {" "}
                            *
                          </span>
                        </H6>
                        <H7 mb={1} style={{ color: theme.palette.primary.red }}>
                          답변 필수 *
                        </H7>
                      </FlexRowAlign>
                      <AppTextField
                        fullWidth
                        name="name"
                        placeholder="이름을 입력해 주세요"
                        value={values.name}
                        onChange={handleChange}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                        sx={{
                          backgroundColor:
                            theme.palette.primary.lightBlueBackground,
                          "& .MuiOutlinedInput-input::placeholder": {
                            fontWeight: 300,
                            fontSize: 15.5,
                            color: "#B5B7C0",
                          },
                        }}
                        inputProps={{ style: { height: 18 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        2. 연락처 남겨주세요
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>
                      <AppTextField
                        fullWidth
                        name="phone"
                        placeholder="연락처를 입력해 주세요"
                        value={values.phone}
                        onChange={handleChange}
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                        sx={{
                          backgroundColor:
                            theme.palette.primary.lightBlueBackground,
                          "& .MuiOutlinedInput-input::placeholder": {
                            fontWeight: 300,
                            fontSize: 15.5,
                            color: "#B5B7C0",
                          },
                        }}
                        inputProps={{ style: { height: 18 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        3. 어떤 경로로 문의를 하시게 되었나요?
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="유튜브" />
                        <SurveyCheckButton title="네이버 (카페, 블로그)" />
                        <SurveyCheckButton title="소개 및 지인" />
                        <SurveyCheckButton title="기타" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        4. 정박사 방송은 언제부터 알게 되셨나요?
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="1주일 이내" />
                        <SurveyCheckButton title="1개월 이내" />
                        <SurveyCheckButton title="1년 이상" />
                        <SurveyCheckButton title="기타" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        5. 금융및 보험관련해서 어느정도 이해도가 있다고
                        생각하시나요?
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="이제 막 관심을 갖기 시작했다" />
                        <SurveyCheckButton title="기본적인 용어정도는 알아 들을수 있다" />
                        <SurveyCheckButton title="보험의 우선순위를 알고, 기준을 잡을수 있다" />
                        <SurveyCheckButton title="기타" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        6. 월보험료는 어느정도 선이면 적정하다고 생각하시나요?
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="10만원이내" />
                        <SurveyCheckButton title="30만원이내" />
                        <SurveyCheckButton title="50만원이상" />
                        <SurveyCheckButton title="기타" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        7. 현재 건강상태에 대해 해당되는 부분이 있다면 체크해
                        주세요
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="3개월이내 검진이나 병원 내원력" />
                        <SurveyCheckButton title="1년이내 재검사 또는 추가검사" />
                        <SurveyCheckButton
                          title="   5년이내 입원,수술,30일이상 투약, 7일이상 치료,
                          10대질병진단 등"
                        />
                        <SurveyCheckButton title="치료사항 없음" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <H6 mb={1}>
                        8. 어떤 보험 설계를 희망하시나요?
                        <span style={{ color: theme.palette.primary.red }}>
                          {" "}
                          *
                        </span>
                      </H6>

                      <Grid container gap={2}>
                        <SurveyCheckButton title="종합형 설계 (단일상품)" />
                        <SurveyCheckButton title="혼합형 설계 (복합+복층설계)" />
                        <SurveyCheckButton title="기타" />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <FlexRowAlign
                        sx={{ justifyContent: "space-between", width: "100%" }}
                      >
                        <H6 mb={1}>9. 기타 특이사항을 남겨주세요</H6>
                      </FlexRowAlign>
                      <AppTextField
                        fullWidth
                        multiline
                        name="etc"
                        placeholder=""
                        value={values.etc}
                        onChange={handleChange}
                        sx={{
                          backgroundColor:
                            theme.palette.primary.lightBlueBackground,
                          "& .MuiOutlinedInput-input::placeholder": {
                            fontWeight: 300,
                            fontSize: 15.5,
                            color: "#B5B7C0",
                          },
                        }}
                        inputProps={{ style: { height: 54 } }}
                      />
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </Grid>

            {/* 두번째 박스 */}
            <Grid item xs={12} mt={2}>
              <Card sx={{ marginTop: "20px" }}>
                <Grid Container py={4} px={isMobile ? 2 : 4}>
                  <Grid item xs={12} mb={5}>
                    <FlexRowAlign
                      gap={1}
                      mt={2}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <FlexRowAlign
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "flex-start",
                        }}
                      >
                        <img src={roundx} width={20} />
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          보험상담 제공을 위해 아래의 정보를 수집하며, 수집된
                          정보는 보험상담 목적외에는 사용되지 않습니다.
                        </TableContent>
                      </FlexRowAlign>
                      <Box
                        ml={isMobile ? 0.5 : 0}
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <TermsModalButton />
                      </Box>
                    </FlexRowAlign>
                  </Grid>

                  <Grid item xs={12}>
                    <FlexBox
                      gap={1}
                      mt={2}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          1.
                        </TableContent>
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          수집하는 개인정보 항목
                        </TableContent>
                      </Box>
                      <Box
                        ml={isMobile ? 3.5 : 0}
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      ></Box>
                    </FlexBox>
                  </Grid>

                  <Grid item xs={12} mb={3}>
                    <FlexBox
                      ml={3.5}
                      gap={1}
                      mt={1}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        // justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <H7 ml={1}>이름, 연락처, 거주지역</H7>
                      </Box>
                    </FlexBox>
                  </Grid>

                  <Grid item xs={12}>
                    <FlexBox
                      gap={1}
                      mt={2}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          2.
                        </TableContent>
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          수집 및 이용목적
                        </TableContent>
                      </Box>
                      <Box
                        ml={isMobile ? 3.5 : 0}
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      ></Box>
                    </FlexBox>
                  </Grid>

                  <Grid item xs={12} mb={3}>
                    <FlexBox
                      ml={3.5}
                      gap={1}
                      mt={1}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        // justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <H7 ml={1}>보험&보상 관련 상담및 서비스정보 제공 등</H7>
                      </Box>
                    </FlexBox>
                  </Grid>

                  <Grid item xs={12}>
                    <FlexBox
                      gap={1}
                      mt={2}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          2.
                        </TableContent>
                        <TableContent
                          ml={1.5}
                          style={{
                            color: theme.palette.primary.dark,
                            whiteSpace: "normal",
                          }}
                        >
                          수집 및 이용목적
                        </TableContent>
                      </Box>
                      <Box
                        ml={isMobile ? 3.5 : 0}
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      ></Box>
                    </FlexBox>
                  </Grid>

                  <Grid item xs={12} mb={3}>
                    <FlexBox
                      ml={3.5}
                      gap={1}
                      mt={1}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        // justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          direction: "row",
                          alignItems: "center",
                        }}
                      >
                        <H7 ml={1}>보험&보상 관련 상담및 서비스정보 제공 등</H7>
                      </Box>
                    </FlexBox>
                  </Grid>

                  <SurveyCheckButton title="동의합니다" />

                  <Grid
                    ml={0}
                    mt={5}
                    container
                    xs={12}
                    flexDirection={"row"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{ width: "100%" }}
                  >
                    <Grid item width={isMobile ? "100%" : 250}>
                      <Button
                        // type="submit"
                        onClick={() => onSubmit()}
                        variant="contained"
                        size="small"
                        sx={{
                          fontSize: 15.5,
                          fontWeight: 500,
                          minWidth: isMobile ? "100%" : 240,
                          width: isMobile ? "100%" : 240,
                          borderRadius: 2,
                          backgroundColor: "primary.darkBlue",
                          "&:hover": {
                            backgroundColor: "primary.darkBlueHover",
                          },
                        }}
                      >
                        제출하기
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Modal>
      )}
    </>
  );
}
